"use client";

import { Gift } from "lucide-react";
import { useEffect, useState } from "react";

import { Badge } from "@/components/ui/badge";
import {
	Tooltip,
	TooltipContent,
	TooltipProvider,
	TooltipTrigger,
} from "@/components/ui/tooltip";
import { useApi } from "@/lib/fetch-client";

interface CreditPool {
	id: string;
	couponCode: string;
	remainingAmount: string;
	modelRestrictions: string[];
	expiresAt: string | null;
}

interface PromoModelBadgeProps {
	orgId: string | null | undefined;
	selectedModel: string;
}

export function PromoModelBadge({
	orgId,
	selectedModel,
}: PromoModelBadgeProps) {
	const api = useApi();
	const [applicableTotal, setApplicableTotal] = useState(0);
	const [applicablePools, setApplicablePools] = useState<CreditPool[]>([]);

	const { data } = api.useQuery(
		"get",
		"/orgs/{id}/credit-pools",
		{
			params: { path: { id: orgId ?? "" } },
		},
		{
			enabled: !!orgId,
			staleTime: 30_000, // 30s cache
		},
	);

	useEffect(() => {
		if (!data) {
			return;
		}
		const pools = (data.creditPools ?? []) as CreditPool[];
		// Derive model ID (strip provider prefix if present e.g. "openai/gpt-4o" → "gpt-4o")
		const modelId = selectedModel.includes("/")
			? (selectedModel.split("/").pop() ?? selectedModel)
			: selectedModel;

		const applicable = pools.filter((p) => {
			const restrictions = (p.modelRestrictions as string[]) ?? [];
			return (
				restrictions.length === 0 ||
				restrictions.some((r) => {
					const rShort = r.includes("/") ? (r.split("/").pop() ?? r) : r;
					return r === selectedModel || r === modelId || rShort === modelId;
				})
			);
		});

		const total = applicable.reduce(
			(sum, p) => sum + parseFloat(p.remainingAmount ?? "0"),
			0,
		);

		setApplicablePools(applicable);
		setApplicableTotal(total);
	}, [data, selectedModel]);

	if (!orgId || applicableTotal <= 0 || selectedModel === "auto") {
		return null;
	}

	return (
		<TooltipProvider>
			<Tooltip>
				<TooltipTrigger asChild>
					<Badge
						variant="outline"
						className="flex items-center gap-1 cursor-default border-border text-foreground bg-muted text-xs"
					>
						<Gift className="h-3.5 w-3.5" />${applicableTotal.toFixed(2)} promo
					</Badge>
				</TooltipTrigger>
				<TooltipContent className="max-w-[220px]">
					<p className="font-medium mb-1.5 text-xs">Promotional Credits</p>
					{applicablePools.map((p) => (
						<div key={p.id} className="flex justify-between gap-4 text-xs">
							<code className="font-mono">{p.couponCode}</code>
							<span>${parseFloat(p.remainingAmount).toFixed(2)}</span>
						</div>
					))}
					<p className="text-xs text-muted-foreground mt-1.5">
						Used before general credits for this model
					</p>
				</TooltipContent>
			</Tooltip>
		</TooltipProvider>
	);
}
